/**
 * The URL state every matter sub-tab shares, and the one read behind all three.
 *
 * The filing date is the organising idea of this screen and is part of the view, so a shared
 * link carries it — along with every other date the user has supplied, because the engine no
 * longer estimates any of them.
 */

import { useParams } from 'react-router';
import { useMatterPanel, useMeta } from '~/api/queries';
import { useUrlState } from '~/hooks/useUrlState';
import { matterForSlug } from '~/lib/routes';
import { useCalculation } from './useCalculation';
import type { FilingLife } from '~/api/schema';
import type { DateParams } from '~/lib/routes';

export const DATE_SPEC = {
  filing: { fallback: '' },
  priority: { fallback: '' },
  publication: { fallback: '' },
  registration: { fallback: '' },
  grant: { fallback: '' },
  asAt: { fallback: '' },
  recordedAt: { fallback: '' },
} as const;

export interface MatterView {
  code: string;
  matter: string;
  lane: string | undefined;
  dates: DateParams;
  setDates: (patch: Partial<Record<keyof typeof DATE_SPEC, string | undefined>>) => void;
  panel: ReturnType<typeof useMatterPanel>;
  meta: ReturnType<typeof useMeta>;
  /**
   * What the dates on screen imply. The panel read takes only the filing date, so once any other
   * known date is supplied this is the calculation's answer rather than the panel's.
   */
  life: FilingLife | null;
  lifePending: boolean;
  /** True while the answer on screen came from the supplied dates rather than the filing alone. */
  lifeFromSuppliedDates: boolean;
}

export function useMatterView(): MatterView {
  const { code = '', matter: slug = '', lane } = useParams();
  const matter = matterForSlug(slug);
  const { values, set } = useUrlState(DATE_SPEC);
  const meta = useMeta();

  const panel = useMatterPanel(code, matter, {
    ...(values.filing ? { filing: values.filing } : {}),
    ...(values.asAt ? { asAt: values.asAt } : {}),
    ...(values.recordedAt ? { recordedAt: values.recordedAt } : {}),
  });

  const dates: DateParams = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== ''),
  );

  const calculation = useCalculation(code, matter, panel.data?.filing ?? '', dates);

  return {
    code,
    matter,
    lane,
    dates,
    setDates: (patch) => set(patch),
    panel,
    meta,
    life: calculation.life ?? panel.data?.life ?? null,
    lifePending: calculation.pending,
    lifeFromSuppliedDates: calculation.life !== null,
  };
}
